import { Link } from "react-router-dom";
import { PlatformIcon, QualityBadge } from "./Badges";
import { money } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useFavorites } from "@/context/FavoritesContext";
import { Amount } from "@/components/shared/Amount";
import { Zap, Droplets, RefreshCw, Heart, MessageSquareText, Radio } from "lucide-react";
import { speedLabel, dropLabel } from "@/lib/labels";
import { detectCustomComments } from "@/lib/customComments";

export function ServiceCard({ service, onOrder }) {
  const { t } = useContent();
  const fav = useFavorites();
  const isFav = fav?.ids?.has(service.id);
  return (
    <div data-testid={`service-card-${service.id}`}
      className="group flex flex-col rounded-xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
            <PlatformIcon icon={service.platform_name?.toLowerCase() === "youtube" ? "youtube" : service.platform_name?.toLowerCase() === "tiktok" ? "music" : service.platform_name?.toLowerCase() === "telegram" ? "send" : "instagram"} className="h-4 w-4 text-foreground" />
          </span>
          <div className="text-xs">
            <div className="font-medium text-foreground">{service.platform_name}</div>
            <div>{service.category_name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {fav && (
            <button data-testid={`fav-btn-${service.id}`} aria-label="favorite" onClick={(e) => { e.preventDefault(); e.stopPropagation(); fav.toggle(service.id); }}
              className="rounded-full p-1.5 transition-colors hover:bg-secondary">
              <Heart className={`h-4 w-4 transition-colors ${isFav ? "fill-red-500 text-red-500" : "text-muted-foreground"}`} />
            </button>
          )}
          <QualityBadge level={service.quality_level} />
        </div>
      </div>

      <h3 className="mt-4 font-display text-base font-semibold leading-snug text-foreground">{service.name}</h3>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {detectCustomComments(service) && (
          <span data-testid={`cc-badge-${service.id}`} className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2.5 py-0.5 text-xs font-medium text-accent">
            <MessageSquareText className="h-3 w-3" /> {t("services.badge.custom_comments", "Commentaires personnalisés")}
          </span>
        )}
        {service.refill_available && (
          <span data-testid={`refill-badge-${service.id}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <RefreshCw className="h-3 w-3" /> {t("services.badge.refill", "Recharge garantie")}
          </span>
        )}
        {/\blive\b|direct/i.test(`${service.name || ""} ${service.name_en || ""} ${service.category_name || ""}`) && (
          <span data-testid={`live-badge-${service.id}`} className="inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 px-2.5 py-0.5 text-xs font-medium text-rose-600 dark:text-rose-400">
            <Radio className="h-3 w-3" /> {t("services.badge.live", "Live")}
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> {speedLabel(t, service.speed_description)}</div>
        <div className="flex items-center gap-1"><Droplets className="h-3.5 w-3.5" /> {t("services.card.drop", "Chute:")} {dropLabel(t, service.drop_level)}</div>
        <div className="flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> {service.refill_available ? `${t("services.card.refill", "Recharge")} ${service.refill_days}${t("services.card.days", "j")}` : t("services.card.no_refill", "Sans recharge")}</div>
      </div>

      <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
        <div>
          <div className="font-display text-xl font-bold text-foreground"><Amount value={service.price_per_1000} /></div>
          <div className="text-xs text-muted-foreground">{t("services.card.per_1000", "par 1 000 · min")} {service.min_quantity.toLocaleString()}</div>
        </div>
        {onOrder ? (
          <button data-testid={`order-btn-${service.id}`} onClick={() => onOrder(service)}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent">
            {t("services.card.order", "Commander")}
          </button>
        ) : (
          <Link to="/register" data-testid={`order-link-${service.id}`}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent">
            {t("services.card.order", "Commander")}
          </Link>
        )}
      </div>
    </div>
  );
}
